import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import { toast } from "react-toastify";

export const Products = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({
    name: "",
    pricePerUnit: "",
    stockQuantity: "",
    visibility: "",
    description: "",
  });

  // ─── New states for image editing ────────────────────────────────
  const [newImages, setNewImages] = useState([]);           // File objects
  const [replaceImages, setReplaceImages] = useState(false); // boolean flag
  const [imagePreview, setImagePreview] = useState([]);     // optional: show previews

  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setErr("");
      const list = await api.products.listMine();
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setErr(e?.data?.error || e?.data?.message || e?.message || "Failed to load products");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [items, query]);

  const startEdit = (p) => {
    setEditingId(p._id);
    setEditFields({
      name: p.name || "",
      pricePerUnit: p.pricePerUnit ?? "",
      stockQuantity: p.stockQuantity ?? "",
      visibility: p.visibility || "local",
      description: p.description || "",
    });
    setNewImages([]);           // reset
    setReplaceImages(false);    // default = append
    setImagePreview([]);        // reset previews
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFields({
      name: "",
      pricePerUnit: "",
      stockQuantity: "",
      visibility: "",
      description: "",
    });
    setNewImages([]);
    setReplaceImages(false);
    setImagePreview([]);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setNewImages(files);

    // Optional: create previews
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreview(previews);

    // Clean up old object URLs when component unmounts or new selection
    return () => previews.forEach(URL.revokeObjectURL);
  };

  const saveEdit = async (id) => {
  try {
    setSaving(true);

    const fields = {
      name: editFields.name?.trim(),
      pricePerUnit: editFields.pricePerUnit ? Number(editFields.pricePerUnit) : undefined,
      stockQuantity: editFields.stockQuantity ? Number(editFields.stockQuantity) : undefined,
      visibility: editFields.visibility,
      description: editFields.description?.trim(),
    };

    // Remove undefined fields so they don't overwrite with empty values
    Object.keys(fields).forEach(key => {
      if (fields[key] === undefined || fields[key] === "") {
        delete fields[key];
      }
    });

    const response = await api.products.update(
      id,
      fields,
      newImages,           // File[]
      replaceImages        // boolean → will be converted to "true"/"false" string
    );

    console.log("Update response:", response); // ← add this temporarily

    toast.success("Product updated successfully");
    await loadProducts();
    cancelEdit();
  } catch (e) {
    console.error("Update error:", e);
    toast.warn(
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      "Failed to update product"
    );
  } finally {
    setSaving(false);
  }
};

  const remove = async (id) => {
    if (!confirm("Remove this product? This cannot be undone.")) return;
    try {
      setDeletingId(id);
      await api.products.remove(id);
      setItems((prev) => prev.filter((p) => p._id !== id));
      toast.success("Product removed");
    } catch (e) {
      console.warn("Delete failed:", e);
      toast.warn(e?.response?.data?.error || "Failed to delete product");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="relative app-container [font-family:'Nunito',Helvetica] text-[#5d4037] pb-40">
        
        {/* Top bar */}
        <div className="w-full flex items-center mt-4">
          <button onClick={() => navigate(-1)} className="ml-4 w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 className="flex-1 text-center font-bold text-[18px]">My Products</h1>
          <div className="w-9 h-9 mr-4"></div>
        </div>

        {/* Search */}
        <div className="px-4 mt-4">
          <div className="relative">
            <input
              placeholder="Search product"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-[44px] pl-10 pr-4 rounded-full border border-[#5d4037] bg-[#fff8e1]"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>
        </div>

        {/* Product List */}
        <div className="px-4 mt-4 space-y-3">
          {loading && <div className="text-sm">Loading…</div>}
          {!!err && <div className="text-sm text-[#c34040]">{err}</div>}
          {!loading && !err && filtered.length === 0 && (
            <div className="text-sm">No products yet.</div>
          )}

          {filtered.map((p) => {
            const thumb = Array.isArray(p.images) && p.images.length ? p.images[0] : "";
            const isEditing = editingId === p._id;

            return (
              <div key={p._id} className="rounded-[12px] bg-white border border-[#eadfb7] p-2">
                <div className="flex items-center">
                  <button
                    onClick={() => navigate(`/products/${p._id}`)}
                    className="flex items-center flex-1 text-left"
                  >
                    <img
                      src={thumb || "https://via.placeholder.com/48x48?text=IMG"}
                      className="w-12 h-12 rounded-[8px] object-cover"
                    />
                    <div className="ml-3">
                      <p className="font-bold leading-5">{p.name}</p>
                      <p className="text-[12px]">₦{Number(p.basePrice).toLocaleString()}</p>
                    </div>
                  </button>

                  <button
                    className="flex flex-col items-center mx-3"
                    onClick={() => remove(p._id)}
                    disabled={deletingId === p._id}
                  >
                    <svg width="18" height="18" fill="#c34040" viewBox="0 0 24 24">
                      <path d="M3 6h18" />
                      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                      <path d="M10 11v6M14 11v6" />
                    </svg>
                    <span className="text-[12px] mt-1">
                      {deletingId === p._id ? "Removing…" : "Remove"}
                    </span>
                  </button>

                  {!isEditing ? (
                    <button onClick={() => startEdit(p)} className="flex flex-col items-center">
                      <svg width="18" height="18" fill="#5d4037" viewBox="0 0 24 24">
                        <path d="M18 2l4 4-10 10H8v-4L18 2z" />
                      </svg>
                      <span className="text-[12px] mt-1">Edit</span>
                    </button>
                  ) : (
                    <button onClick={cancelEdit} className="flex flex-col items-center">
                      <span className="underline text-[12px]">Cancel</span>
                    </button>
                  )}
                </div>

                {/* Editing panel */}
                {isEditing && (
                  <div className="mt-3 p-3 rounded-[10px] bg-[#fff8e1] border">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="text-[12px] font-semibold">Name</label>
                        <input
                          className="w-full h-[40px] border px-3 rounded-[10px]"
                          value={editFields.name}
                          onChange={(e) => setEditFields((f) => ({ ...f, name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-[12px] font-semibold">Price</label>
                        <input
                          className="w-full h-[40px] border px-3 rounded-[10px]"
                          value={editFields.pricePerUnit}
                          onChange={(e) => setEditFields((f) => ({ ...f, pricePerUnit: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-[12px] font-semibold">Stock</label>
                        <input
                          className="w-full h-[40px] border px-3 rounded-[10px]"
                          value={editFields.stockQuantity}
                          onChange={(e) => setEditFields((f) => ({ ...f, stockQuantity: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-[12px] font-semibold">Visibility</label>
                        <select
                          className="w-full h-[40px] border px-3 rounded-[10px]"
                          value={editFields.visibility}
                          onChange={(e) => setEditFields((f) => ({ ...f, visibility: e.target.value }))}
                        >
                          <option value="local">local</option>
                          <option value="global">global</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="text-[12px] font-semibold">Description</label>
                        <textarea
                          rows={3}
                          className="w-full border px-3 py-2 rounded-[10px]"
                          value={editFields.description}
                          onChange={(e) => setEditFields((f) => ({ ...f, description: e.target.value }))}
                        />
                      </div>

                      {/* ─── New: Image Update Section ─── */}
                      <div className="col-span-2 mt-2">
                        <label className="text-[12px] font-semibold block mb-1">Product Images</label>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageChange}
                          className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#f7c945] file:text-[#5d4037] hover:file:bg-[#e0b42e]"
                        />
                        <p className="text-xs mt-1 text-gray-600">
                          {newImages.length > 0
                            ? `${newImages.length} new image(s) selected`
                            : "Upload new images (optional)"}
                        </p>

                        {/* Replace / Append toggle */}
                        {newImages.length > 0 && (
                          <div className="mt-2 flex items-center gap-3">
                            <label className="text-sm flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={replaceImages}
                                onChange={(e) => setReplaceImages(e.target.checked)}
                                className="w-4 h-4 accent-[#f7c945]"
                              />
                              Replace all existing images (instead of adding)
                            </label>
                          </div>
                        )}

                        {/* Optional preview */}
                        {imagePreview.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {imagePreview.map((src, i) => (
                              <img
                                key={i}
                                src={src}
                                alt="preview"
                                className="w-16 h-16 object-cover rounded-md border"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 flex justify-end gap-8">
                      <button className="text-[#c34040] underline" onClick={cancelEdit}>
                        Cancel
                      </button>
                      <button
                        onClick={() => saveEdit(p._id)}
                        disabled={saving}
                        className={`px-6 h-[36px] rounded-[18px] font-semibold ${
                          saving ? "bg-[#8f814f] opacity-80" : "bg-[#f7c945] hover:bg-[#e0b42e]"
                        }`}
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom nav */}
        <div className="fixed bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16">
          <div className="px-6 pt-2 pb-4 bg-white border-t border-[#eadfb7]">
            <div className="grid grid-cols-4 gap-4 text-[12px] text-[#5d4037]">
              <div className="flex flex-col items-center" onClick={() => navigate("/dashboard")}>
                <div className="w-9 h-9 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M3 10.5L12 4l9 6.5" />
                    <path d="M5 10v10h14V10" />
                  </svg>
                </div>
                <span className="mt-1 text-[#5d4037]/80">Overview</span>
              </div>

              <div className="flex flex-col items-center" onClick={() => navigate("/orders")}>
                <div className="w-9 h-9 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="4" y="3" width="16" height="18" rx="2" />
                    <path d="M8 7h8M8 11h6M8 15h8" />
                  </svg>
                </div>
                <span className="mt-1 text-[#5d4037]/80">Orders</span>
              </div>
                <div className="flex flex-col items-center" onClick={() => navigate("/products/new")}>
                  <div className="w-9 h-9 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                    <div className="w-6 h-6 rounded-full border border-[#5d4037] grid place-items-center bg-[#f7c945]">
                      <span className="text-[11px] font-bold leading-none text-[#5d4037]">P</span>
                    </div>
                  </div>
                  <span className="mt-1 font-semibold">Add Products</span>
                </div>

              <div className="flex flex-col items-center" onClick={() => navigate("/profile")}>
                <div className="w-9 h-9 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                    <path d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                    <path d="M20 21v-2a6 6 0 00-6-6H10a6 6 0 00-6 6v2" />
                  </svg>
                </div>
                <span className="mt-1 text-[#5d4037]/80">Profile</span>
              </div>
            </div>
          </div>
          <div className="h-[22px] bg-[#5d4037] flex items-center justify-center">
            <div className="w-[120px] h-[6px] rounded-full bg-white" />
          </div>
        </div>
      </div>
    </div>
  );
};